import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Badge from 'components/field/Badge';
import EnumSelect from 'components/field/EnumSelect';
import SearchInput from 'components/field/SearchInput';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';

import useDebounce from 'hooks/useDebounce';

import { ENUMS } from 'services/consts';

import ModuleCard from './ModuleCard';
import * as S from './OTOModules.styled';

const defaultPlans = ['FreePackage', 'StarterPackage', 'ScalePackage', 'EnterprisePackage'];
const defaultCategories = [
	'shippingManagement',
	'warehouseAndInventoryManagement',
	'orderManagement',
	'customerNotifications',
	'accessControl'
];

const Modules = () => {
	const [searchValue, setSearchValue] = useState('');
	const { modulesList } = useSelector((state) => state.otoPackageList);
	const user = useSelector((state) => state.user);
	const { t } = useTranslation();

	const debouncedValue = useDebounce(searchValue, 500);
	const [selectedPlans, setSelectedPlans] = useState(defaultPlans);
	const [selectedCategories, setSelectedCategories] = useState(defaultCategories);
	const { hash } = useLocation();

	const filteredList = useMemo(() => {
		if (!modulesList) return [];

		return modulesList
			.map((plan) => {
				// Filter by plan if any plans are selected
				if (selectedPlans?.length > 0 && !selectedPlans?.includes(plan.name)) {
					return null;
				}

				// Filter modules within each plan
				const filteredModules = plan.modules.filter((module) => {
					// Filter by search term
					const searchTerm = debouncedValue?.toLowerCase();
					const matchesSearch = !debouncedValue || t(module.slug).toLowerCase().includes(searchTerm);

					// Filter by category
					const matchesCategory =
						!selectedCategories ||
						selectedCategories.length === 0 ||
						(module.category && selectedCategories.includes(module.category));

					// Both filters must match
					return matchesSearch && matchesCategory;
				});

				if (filteredModules.length === 0) {
					return null;
				}

				return {
					...plan,
					modules: filteredModules
				};
			})
			.filter(Boolean); // Remove null plans
	}, [modulesList, debouncedValue, selectedPlans, selectedCategories]);

	useEffect(() => {
		if (hash) {
			const element = document.getElementById(hash.substring(1)); // Remove #
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [hash]);

	return (
		<S.PlansContainer>
			<div>
				<div className="mb-5">
					<Typography variant="p2" fontWeight="700" className="mb-3">
						otoModules
					</Typography>
					<Typography variant="p3" color="var(--content-2)">
						otoModulesDescription
					</Typography>
				</div>
				<S.SearchContainer>
					<SearchInput setValue={setSearchValue} value={searchValue} placeholder="searchOTOModules" />
					<S.FiltersContainer>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.CLIENT_PLANS}
							multipleSelection
							placeholder="plans"
							value={selectedPlans}
							valueRemovable={false}
							setValue={setSelectedPlans}
							renderSelectionValue={(value, data) => {
								return (
									<Typography fontWeight="500" color="var(--black)">
										{value.split(',').length === data.length ||
										value.split(',').length === defaultPlans.length
											? t('allPlans')
											: value}
									</Typography>
								);
							}}
						/>
						<EnumSelect
							placeholder="categories"
							enumName={ENUMS.MODULE_CATEGORIES}
							multipleSelection
							valueRemovable={false}
							value={selectedCategories}
							setValue={setSelectedCategories}
							renderSelectionValue={(value, data) => (
								<Typography fontWeight="500" color="var(--black)">
									{value.split(',').length === data.length ||
									value.split(',').length === defaultCategories.length
										? t('allCategories')
										: value}
								</Typography>
							)}
						/>
					</S.FiltersContainer>
				</S.SearchContainer>
			</div>
			{modulesList.length > 0 ? (
				filteredList.map((plan) => (
					<S.Plan key={plan.packageRoleID} id={plan.packageRoleID}>
						<S.PlanHeader>
							<Typography variant="p2" fontWeight="700">
								{t(plan.name)} ({plan.modules.length})
							</Typography>
							{plan.name === user.nextPackageName && (
								<Badge variant="pill" size="small" color="blue" text="current" />
							)}
						</S.PlanHeader>
						<S.ModulesContainer>
							{plan.modules.map((module) => (
								<ModuleCard key={module.id} module={module} />
							))}
						</S.ModulesContainer>
					</S.Plan>
				))
			) : (
				<NoRecordsFound
					title="noModulesFoundTitle"
					subTitle="noModulesFoundSubTitle"
					lottieName="noResultsFound"
				/>
			)}
		</S.PlansContainer>
	);
};

export default Modules;
